import React, { useState, useEffect } from 'react';
import { safeUrl } from '../lib/safeUrl';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { pathId } from '../lib/pathId';
import SEOHead from '../components/SEOHead';
import { 
  ArrowLeft, Building2, MapPin, DollarSign, Users, 
  Edit2, Save, X, Calendar, TrendingUp, FileText,
  Briefcase, Link2, Clock, Navigation, User, ExternalLink, Heart,
  ChevronDown, ChevronRight, Loader2, Plus, CalendarDays, UserPlus, CheckCircle2
} from 'lucide-react';
import FavoriteButton from '../components/FavoriteButton';

import ManageLinksDialog from '../components/dialogs/ManageLinksDialog';
import { getRegionsForCountry } from '../data/regions';
import { projectSectorsOf, getIndustryLabel, MAX_PROJECT_SECTORS } from '../constants/industries'; // WO-73
import IndustryMultiSelectFilter from '../components/IndustryMultiSelectFilter';
import YearPicker, { formatYear } from '../components/YearPicker';
import ResearchNote from '../components/ResearchNote'; // WO-60

const STAKEHOLDER_ROLES = ['Developer', 'Contractor', 'Consultant', 'Supplier', 'Others'];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

const stageColors = {
  concept: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  design: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  tender: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  execution: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  complete: 'bg-gray-500/10 text-gray-500 border-gray-500/30'
};

const stageProgress = {
  concept: 10,
  design: 30,
  tender: 50,
  execution: 75,
  complete: 100
};

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const { getToken, user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [isFavorite, setIsFavorite] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null); // 'tenders' | 'companies' | 'master_projects' | 'sub_projects' | null
  const [linkedTenderDetails, setLinkedTenderDetails] = useState([]);
  const [linkedCompanyDetails, setLinkedCompanyDetails] = useState([]);
  const [linkedMasterProjectDetails, setLinkedMasterProjectDetails] = useState([]);
  const [subProjects, setSubProjects] = useState([]);
  const [subProjectCount, setSubProjectCount] = useState(0);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [showManageLinks, setShowManageLinks] = useState(false);
  const [newStakeholderRole, setNewStakeholderRole] = useState('Developer');
  const [newStakeholderName, setNewStakeholderName] = useState('');

  const isAdmin = user?.role === 'admin';
  const token = getToken();
  const [savingLead, setSavingLead] = useState(false);
  const [savedAsLead, setSavedAsLead] = useState(false);

  const handleExtractAsLead = async () => {
    setSavingLead(true);
    try {
      const response = await fetch(`${API_URL}/api/opportunities/from-project/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to extract lead');
      setSavedAsLead(true);
      if (data.already_in_crm) {
        toast.info(isRTL ? 'هذا المشروع موجود بالفعل في CRM الخاص بك' : 'This project is already in your CRM');
      } else {
        toast.success(isRTL ? 'تمت إضافة المشروع إلى CRM الخاص بك كعميل محتمل' : 'Project added to your CRM as a lead');
      }
    } catch (error) {
      console.error('Error extracting lead:', error);
      toast.error(error.message);
    } finally {
      setSavingLead(false);
    }
  };
  const translations = {
    en: {
      backToProjects: 'Back to Projects',
      projectDetails: 'Project Details',
      editProject: 'Edit Project',
      save: 'Save',
      cancel: 'Cancel',
      name: 'Project Name',
      country: 'Country',
      city: 'City',
      region: 'Region / Province',
      sector: 'Industry',
      stage: 'Stage',
      estValue: 'Estimated Value',
      stakeholders: 'Stakeholders',
      linkedNews: 'Linked News',
      linkedTenders: 'Linked Tenders',
      linkedCompanies: 'Linked Companies',
      linkedMasterProjects: 'Linked Master Projects',
      masterProjectBadge: 'Master Project',
      subProjects: 'Sub-Projects',
      createdAt: 'Created',
      updatedAt: 'Last Updated',
      progress: 'Progress',
      description: 'Description',
      descriptionPlaceholder: 'Brief project description (up to 500 words)...',
      words: 'words',
      startDate: 'Start Date',
      endDate: 'End Date',
      tentative: 'Tentative',
      loading: 'Loading project...',
      notFound: 'Project not found',
      saveSuccess: 'Project updated successfully',
      saveError: 'Failed to update project',
      adminOnly: 'Admin only',
      concept: 'Concept',
      design: 'Design',
      tender: 'Tender',
      execution: 'Execution',
      complete: 'Completed',
      noStakeholders: 'No stakeholders listed'
    },
    ar: {
      backToProjects: 'العودة للمشاريع',
      projectDetails: 'تفاصيل المشروع',
      editProject: 'تعديل المشروع',
      save: 'حفظ',
      cancel: 'إلغاء',
      name: 'اسم المشروع',
      country: 'الدولة',
      city: 'المدينة',
      region: 'المنطقة / الإمارة',
      sector: 'الصناعة',
      stage: 'المرحلة',
      estValue: 'القيمة التقديرية',
      stakeholders: 'أصحاب المصلحة',
      linkedNews: 'الأخبار المرتبطة',
      linkedTenders: 'المناقصات المرتبطة',
      linkedCompanies: 'الشركات المرتبطة',
      linkedMasterProjects: 'المشاريع الرئيسية المرتبطة',
      masterProjectBadge: 'مشروع رئيسي',
      subProjects: 'المشاريع الفرعية',
      createdAt: 'تاريخ الإنشاء',
      updatedAt: 'آخر تحديث',
      progress: 'التقدم',
      description: 'الوصف',
      descriptionPlaceholder: 'وصف موجز للمشروع (حتى 500 كلمة)...',
      words: 'كلمة',
      startDate: 'تاريخ البدء',
      endDate: 'تاريخ الانتهاء',
      tentative: 'تقديري',
      loading: 'جاري تحميل المشروع...',
      notFound: 'المشروع غير موجود',
      saveSuccess: 'تم تحديث المشروع بنجاح',
      saveError: 'فشل تحديث المشروع',
      adminOnly: 'للمسؤولين فقط',
      concept: 'مفهوم',
      design: 'تصميم',
      tender: 'مناقصة',
      execution: 'تنفيذ',
      complete: 'مكتمل',
      noStakeholders: 'لا يوجد أصحاب مصلحة مدرجين'
    }
  };

  const txt = translations[language];

  const fetchProject = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/intelligence/projects/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          setError(txt.notFound);
        } else if (response.status === 402) {
          const body = await response.json().catch(() => ({}));
          setError(body?.detail || body?.plan_gate?.message || txt.notFound);
        } else {
          throw new Error('Failed to fetch project');
        }
        return;
      }
      
      const data = await response.json();
      setProject(data);
      if (data.is_master_project) {
        fetchSubProjectCount(data._id);
      }
      setEditData({
        canonical_name: data.canonical_name || '',
        country: data.country || 'SA',
        city: data.city || '',
        region: data.region || '',
        sectors: projectSectorsOf(data),
        stage: data.stage || 'concept',
        location_url: data.location_url || '',
        value_min: (data.value_estimate?.min != null) ? String(data.value_estimate.min) : '',
        value_max: (data.value_estimate?.max != null) ? String(data.value_estimate.max) : '',
        currency: 'USD',
        stakeholders: data.stakeholders ? [...data.stakeholders] : [],
        is_master_project: data.is_master_project || false,
        progress: (data.progress ?? stageProgress[data.stage] ?? 0),
        description: data.description || '',
        start_date: data.start_date || '',
        end_date: data.end_date || '',
      });
    } catch (err) {
      console.error('Error fetching project:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProject();
    }
  }, [id]);

  const fetchLinkedTenders = async (tenderIds) => {
    if (!tenderIds?.length) { setLinkedTenderDetails([]); return; }
    setLoadingLinked(true);
    try {
      const results = await Promise.all(
        tenderIds.map(tid =>
          fetch(`${API_URL}/api/intelligence/tenders/${pathId(tid)}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }).then(r => r.ok ? r.json() : null)
        )
      );
      setLinkedTenderDetails(results.filter(Boolean));
    } catch (err) {
      console.error('Error fetching linked tenders:', err);
    } finally {
      setLoadingLinked(false);
    }
  };

  const fetchLinkedCompanies = async (companyIds) => {
    if (!companyIds?.length) { setLinkedCompanyDetails([]); return; }
    setLoadingLinked(true);
    try {
      const results = await Promise.all(
        companyIds.map(cid =>
          fetch(`${API_URL}/api/intelligence/companies/${cid}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }).then(r => r.ok ? r.json() : null)
        )
      );
      setLinkedCompanyDetails(results.filter(Boolean));
    } catch (err) {
      console.error('Error fetching linked companies:', err);
    } finally {
      setLoadingLinked(false);
    }
  };

  const fetchSubProjectCount = async (masterId) => {
    try {
      const res = await fetch(`${API_URL}/api/intelligence/projects?master_project_id=${encodeURIComponent(masterId)}&page_size=1`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setSubProjectCount(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching sub-project count:', err);
    }
  };

  const fetchSubProjects = async (masterId) => {
    setLoadingLinked(true);
    try {
      const res = await fetch(`${API_URL}/api/intelligence/projects?master_project_id=${encodeURIComponent(masterId)}&page_size=50`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setSubProjects(data.items || []);
        setSubProjectCount(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching sub-projects:', err);
    } finally {
      setLoadingLinked(false);
    }
  };

  const fetchLinkedMasterProjects = async (masterProjectIds) => {    if (!masterProjectIds?.length) { setLinkedMasterProjectDetails([]); return; }
    setLoadingLinked(true);
    try {
      const results = await Promise.all(
        masterProjectIds.map(pid =>
          fetch(`${API_URL}/api/intelligence/projects/${encodeURIComponent(pid)}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }).then(r => r.ok ? r.json() : null)
        )
      );
      setLinkedMasterProjectDetails(results.filter(Boolean));
    } catch (err) {
      console.error('Error fetching linked master projects:', err);
    } finally {
      setLoadingLinked(false);
    }
  };

  const toggleLinkedSection = (section, proj) => {
    if (expandedSection === section) {
      setExpandedSection(null);
      return;
    }
    setExpandedSection(section);
    if (section === 'tenders') fetchLinkedTenders(proj?.linked_tender_ids);
    else if (section === 'companies') fetchLinkedCompanies(proj?.linked_company_ids);
    else if (section === 'master_projects') fetchLinkedMasterProjects(proj?.linked_master_project_ids);
    else if (section === 'sub_projects') fetchSubProjects(proj?._id);
  };
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!id || !token) return;
      try {
        const response = await fetch(
          `${API_URL}/api/admin/favorites/check?item_type=project&item_id=${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setIsFavorite(data.is_favorite);
        }
      } catch (err) {
        console.error('Error checking favorite status:', err);
      }
    };
    checkFavoriteStatus();
  }, [id, token]);

  const handleSave = async () => {
    const isCreator = !!(project?.created_by && user?.id && project.created_by === user.id);
    if (!isAdmin && !isCreator) {
      toast.error(txt.adminOnly);
      return;
    }
    
    setSaving(true);
    
    try {
      const token = getToken();
      // Budget inputs are in millions (Mill. USD) — stored verbatim as millions.
      const payload = {
        ...editData,
        value_min: editData.value_min ? parseFloat(editData.value_min) : null,
        value_max: editData.value_max ? parseFloat(editData.value_max) : null,
      };
      const response = await fetch(`${API_URL}/api/intelligence/projects/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update project');
      }
      
      const result = await response.json();
      setProject(result);
      setIsEditing(false);
      toast.success(txt.saveSuccess);
    } catch (err) {
      console.error('Error updating project:', err);
      toast.error(txt.saveError);
    } finally {
      setSaving(false);
    }
  };

  const formatValue = (valueEstimate) => {
    if (!valueEstimate) return '-';
    const { min, max, currency } = valueEstimate;
    // Budgets are stored in MILLIONS of USD (Mill USD). Scale for display.
    const formatNum = (m) => {
      if (m === null || m === undefined || m === '') return null;
      const n = Number(m);
      if (isNaN(n)) return null;
      if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}T`;
      if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}B`;
      return `$${n.toLocaleString()}M`;
    };
    if (min && max) {
      return `${formatNum(min)} - ${formatNum(max)}`;
    }
    const formatted = formatNum(min || max);
    return formatted || '-';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="project-detail-loading">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Card className="border-0 bg-card/50">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="project-detail-error">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard/projects')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {txt.backToProjects}
        </Button>
        <Card className="border-0 bg-card/50">
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">{error || txt.notFound}</h3>
            <Button variant="outline" onClick={() => navigate('/dashboard/projects')}>
              {txt.backToProjects}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = project.progress ?? stageProgress[project.stage] ?? 0;

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="project-detail-page">
      <SEOHead 
        type="projects" 
        id={id} 
        fallback={{
          title: `${project.name || project.canonical_name} | Mid2Bid Projects`,
          description: `Infrastructure project in ${project.country}. Status: ${project.stage}. Track on Mid2Bid.`
        }}
      />
      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard/projects')}
          className={`flex items-center gap-2 w-fit ${isRTL ? 'flex-row-reverse' : ''}`}
          data-testid="back-to-projects-btn"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
          {txt.backToProjects}
        </Button>
        
        {(isAdmin || (project?.created_by && user?.id && project.created_by === user.id)) && (
          <div className="flex flex-wrap gap-2">
            {isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditing(false)}
                  disabled={saving}
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  {txt.cancel}
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-project-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {txt.save}
                </Button>
              </>
            ) : (
              <>
                <FavoriteButton
                  itemType="project"
                  itemId={id}
                  isFavorite={isFavorite}
                  onToggle={(newState) => setIsFavorite(newState)}
                  token={token}
                />
                <Button
                  variant="outline"
                  onClick={handleExtractAsLead}
                  disabled={savingLead || savedAsLead}
                  data-testid="extract-lead-project-btn"
                >
                  {savingLead ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : savedAsLead ? (
                    <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2 text-emerald-500" />
                  )}
                  {savedAsLead
                    ? (isRTL ? 'في CRM' : 'In your CRM')
                    : (isRTL ? 'استخراج كعميل محتمل' : 'Extract as Lead')}
                </Button>
                <Button 
                  onClick={() => setIsEditing(true)}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="edit-project-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  {txt.editProject}
                </Button>
              </>
            )}
          </div>
        )}
        {!isAdmin && (
          <div className={`flex flex-wrap gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button
              variant="outline"
              onClick={handleExtractAsLead}
              disabled={savingLead || savedAsLead}
              data-testid="extract-lead-project-btn"
            >
              {savingLead ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : savedAsLead ? (
                <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2 text-emerald-500" />
              )}
              {savedAsLead
                ? (isRTL ? 'في CRM' : 'In your CRM')
                : (isRTL ? 'استخراج كعميل محتمل' : 'Extract as Lead')}
            </Button>
            <FavoriteButton
              itemType="project"
              itemId={id}
              isFavorite={isFavorite}
              onToggle={(newState) => setIsFavorite(newState)}
              token={token}
            />
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Project Info Card */}
        <Card className="lg:col-span-2 border-0 bg-card/50 backdrop-blur-sm overflow-hidden">
          {/* Progress bar at top */}
          <div className="h-2 w-full bg-muted">
            <div 
              className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          <CardHeader>
            <CardTitle className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Building2 className="w-5 h-5 text-primary" />
              {txt.projectDetails}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Header Section */}
            <div className={`flex items-start justify-between gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                {/* Reference ID */}
                {project.ref_id && (
                  <Badge className="bg-primary text-primary-foreground font-mono text-sm mb-2" data-testid="project-detail-ref-id">
                    {project.ref_id}
                  </Badge>
                )}
                {isEditing ? (
                  <Input
                    value={editData.canonical_name}
                    onChange={(e) => setEditData({ ...editData, canonical_name: e.target.value })}
                    className="text-2xl font-bold h-12"
                    data-testid="edit-name-input"
                  />
                ) : (
                  <h2 className="text-2xl font-bold">{project.canonical_name}</h2>
                )}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                {project.is_master_project && (
                  <Badge className="bg-blue-600 text-white font-semibold gap-1" data-testid="master-project-badge">
                    <Building2 className="w-3 h-3" />
                    {txt.masterProjectBadge}
                  </Badge>
                )}
                <Badge className={`${stageColors[project.stage]} capitalize`}>
                  {txt[project.stage] || project.stage}
                </Badge>
              </div>
            </div>

            <Separator />

            {/* Project Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Country */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <MapPin className="w-4 h-4" />
                  {txt.country}
                </Label>
                {isEditing ? (
                  <Select
                    value={editData.country || ''}
                    onValueChange={(value) => setEditData({ ...editData, country: value, region: '' })}
                  >
                    <SelectTrigger data-testid="edit-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        { code: 'SA', en: 'Saudi Arabia' }, { code: 'AE', en: 'UAE' },
                        { code: 'OM', en: 'Oman' }, { code: 'KW', en: 'Kuwait' },
                        { code: 'BH', en: 'Bahrain' }, { code: 'QA', en: 'Qatar' },
                        { code: 'EG', en: 'Egypt' }, { code: 'JO', en: 'Jordan' },
                        { code: 'IQ', en: 'Iraq' }, { code: 'LB', en: 'Lebanon' },
                        { code: 'YE', en: 'Yemen' }, { code: 'MA', en: 'Morocco' },
                        { code: 'TR', en: 'Turkey' }, { code: 'PK', en: 'Pakistan' },
                        { code: 'IN', en: 'India' },
                      ].map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-medium">
                    {project.country ? (
                      <>{countryFlags[project.country]} {countryNames[language][project.country]}</>
                    ) : '-'}
                  </p>
                )}
              </div>

              {/* Region */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <MapPin className="w-4 h-4" />
                  {txt.region}
                </Label>
                {isEditing ? (
                  getRegionsForCountry(editData.country || project.country) ? (
                    <Select
                      value={editData.region || ''}
                      onValueChange={(value) => setEditData({ ...editData, region: value })}
                    >
                      <SelectTrigger data-testid="edit-region-select">
                        <SelectValue placeholder={language === 'ar' ? 'اختر المنطقة...' : 'Select region...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {getRegionsForCountry(editData.country || project.country).map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={editData.region || ''}
                      onChange={(e) => setEditData({ ...editData, region: e.target.value })}
                      placeholder={language === 'ar' ? 'المنطقة / الإقليم' : 'Region / Province'}
                      data-testid="edit-region-input"
                    />
                  )
                ) : (
                  <p className="font-medium">{project.region || '-'}</p>
                )}
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <MapPin className="w-4 h-4" />
                  {txt.city}
                </Label>
                {isEditing ? (
                  <Input
                    value={editData.city}
                    onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                    data-testid="edit-city-input"
                  />
                ) : (
                  <p className="font-medium">{project.city || '-'}</p>
                )}
              </div>

              {/* Location URL — full-width row */}
              <div className="space-y-2 md:col-span-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Navigation className="w-4 h-4" />
                  {language === 'ar' ? 'رابط الموقع (Google Maps)' : 'Location URL (Google Maps)'}
                </Label>
                {isEditing ? (
                  <Input
                    value={editData.location_url || ''}
                    onChange={(e) => setEditData({ ...editData, location_url: e.target.value })}
                    placeholder="https://maps.google.com/..."
                    data-testid="edit-location-url-input"
                  />
                ) : (
                  safeUrl(project.location_url) ? (
                    <a
                      href={safeUrl(project.location_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-500 hover:text-blue-700 hover:underline text-sm font-medium"
                    >
                      <Navigation className="w-4 h-4" />
                      {language === 'ar' ? 'عرض على الخريطة' : 'View on Map'}
                    </a>
                  ) : <p className="text-sm text-muted-foreground">-</p>
                )}
              </div>

              {/* Start Date + End Date — full-width row */}
              <div className="md:col-span-2 grid grid-cols-2 gap-4">
                {/* Start Date */}
                <div className="space-y-1.5">
                  <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CalendarDays className="w-4 h-4" />
                    {txt.startDate}
                    <span className="text-xs font-normal text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded">
                      {txt.tentative}
                    </span>
                  </Label>
                  {isEditing ? (
                    <YearPicker
                      value={editData.start_date || ''}
                      onChange={(v) => setEditData({ ...editData, start_date: v })}
                      language={language}
                      data-testid="edit-start-date-picker"
                    />
                  ) : (
                    <p className="font-medium">
                      {formatYear(project.start_date) || '-'}
                    </p>
                  )}
                </div>
                {/* End Date */}
                <div className="space-y-1.5">
                  <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CalendarDays className="w-4 h-4" />
                    {txt.endDate}
                    <span className="text-xs font-normal text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded">
                      {txt.tentative}
                    </span>
                  </Label>
                  {isEditing ? (
                    <YearPicker
                      value={editData.end_date || ''}
                      onChange={(v) => setEditData({ ...editData, end_date: v })}
                      language={language}
                      data-testid="edit-end-date-picker"
                    />
                  ) : (
                    <p className="font-medium">
                      {formatYear(project.end_date) || '-'}
                    </p>
                  )}
                </div>
              </div>

              {/* Sector / Industry */}
              <div className="space-y-2 md:col-span-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Briefcase className="w-4 h-4" />
                  {txt.sector}
                </Label>
                {isEditing ? (
                  <IndustryMultiSelectFilter
                    selected={editData.sectors || []}
                    onChange={(vals) => setEditData({ ...editData, sectors: vals })}
                    language={language}
                    scope="projects"
                    max={MAX_PROJECT_SECTORS}
                    data-testid="edit-sectors-select"
                  />
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {/* WO-73: project sectors only, main sector first and highlighted */}
                    {projectSectorsOf(project).map((id, i) => (
                      <Badge key={id} variant={i === 0 ? 'default' : 'secondary'} className="text-xs">
                        {getIndustryLabel(id, language)}
                      </Badge>
                    ))}
                    {projectSectorsOf(project).length === 0 && <p className="text-sm text-muted-foreground">-</p>}
                  </div>
                )}
              </div>

              {/* Stage */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <TrendingUp className="w-4 h-4" />
                  {txt.stage}
                </Label>
                {isEditing ? (
                  <select
                    value={editData.stage}
                    onChange={(e) => setEditData({ ...editData, stage: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    data-testid="edit-stage-select"
                  >
                    <option value="concept">{txt.concept}</option>
                    <option value="design">{txt.design}</option>
                    <option value="tender">{txt.tender}</option>
                    <option value="execution">{txt.execution}</option>
                    <option value="complete">{txt.complete}</option>
                  </select>
                ) : (
                  <Badge className={`${stageColors[project.stage]} capitalize`}>
                    {txt[project.stage] || project.stage}
                  </Badge>
                )}
              </div>

              {/* Estimated Value */}
              <div className="space-y-2 md:col-span-2">
                <Label className={`flex items-center justify-between gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    {txt.estValue}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-700/50 rounded px-2 py-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {language === 'ar' ? 'القيم بملايين الدولارات (مليون USD)' : 'Values in millions of USD (Mill. USD)'}
                  </span>
                </Label>
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{language === 'ar' ? 'الحد الأدنى (مليون USD)' : 'Min (Mill. USD)'}</Label>
                      <Input
                        type="number"
                        value={editData.value_min || ''}
                        onChange={(e) => setEditData({ ...editData, value_min: e.target.value })}
                        placeholder="e.g. 1"
                        data-testid="edit-value-min-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{language === 'ar' ? 'الحد الأقصى (مليون USD)' : 'Max (Mill. USD)'}</Label>
                      <Input
                        type="number"
                        value={editData.value_max || ''}
                        onChange={(e) => setEditData({ ...editData, value_max: e.target.value })}
                        placeholder="e.g. 5"
                        data-testid="edit-value-max-input"
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-accent">
                    {formatValue(project.value_estimate)}
                  </p>
                )}
              </div>

              {/* Master Project Toggle */}
              <div className="space-y-2 md:col-span-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Building2 className="w-4 h-4" />
                  {txt.masterProjectBadge}
                </Label>
                {isEditing ? (
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={editData.is_master_project || false}
                      onCheckedChange={(checked) => setEditData({ ...editData, is_master_project: checked })}
                      data-testid="edit-master-project-toggle"
                    />
                    <span className="text-sm text-muted-foreground">
                      {editData.is_master_project
                        ? (language === 'ar' ? 'نعم — مشروع رئيسي' : 'Yes — this is a Master Project')
                        : (language === 'ar' ? 'لا' : 'No')}
                    </span>
                  </div>
                ) : (
                  project.is_master_project ? (
                    <Badge className="bg-blue-600 text-white font-semibold gap-1" data-testid="master-project-field-badge">
                      <Building2 className="w-3 h-3" />
                      {txt.masterProjectBadge}
                    </Badge>
                  ) : (
                    <p className="text-sm text-muted-foreground">{language === 'ar' ? 'لا' : 'No'}</p>
                  )
                )}
              </div>
            </div>

            <Separator />

            {/* Description */}
            <Separator />
            <div className="space-y-2">
              <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                <FileText className="w-4 h-4" />
                {txt.description}
              </Label>
              {isEditing ? (
                <div className="space-y-1">
                  <Textarea
                    value={editData.description || ''}
                    onChange={(e) => {
                      const text = e.target.value;
                      const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
                      if (wordCount <= 500) setEditData({ ...editData, description: text });
                    }}
                    placeholder={txt.descriptionPlaceholder}
                    className="min-h-[120px] resize-y text-sm"
                    data-testid="edit-description-textarea"
                  />
                  <p className={`text-xs text-right ${
                    (editData.description?.trim().split(/\s+/).filter(Boolean).length || 0) > 450
                      ? 'text-orange-500 font-medium'
                      : 'text-muted-foreground'
                  }`}>
                    {editData.description?.trim().split(/\s+/).filter(Boolean).length || 0} / 500 {txt.words}
                  </p>
                </div>
              ) : (
                project.description ? (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="project-description">
                    {project.description}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">-</p>
                )
              )}
            </div>

            {/* WO-60 — ANTHONY's latest check, with its sources */}
            {!isEditing && <ResearchNote item={project} language={language} />}

            {/* Progress Section */}
            <div className="space-y-3">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Label className="text-muted-foreground">{txt.progress}</Label>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={editData.progress ?? progress}
                      onChange={(e) => setEditData({ ...editData, progress: Number(e.target.value) })}
                      className="w-32 accent-primary cursor-pointer"
                      data-testid="edit-progress-slider"
                    />
                    <span className="font-bold text-lg w-12 text-right" data-testid="edit-progress-value">
                      {editData.progress ?? progress}%
                    </span>
                  </div>
                ) : (
                  <span className="font-bold text-lg">{progress}%</span>
                )}
              </div>
              <Progress value={isEditing ? (editData.progress ?? progress) : progress} className="h-3" />
            </div>

            {/* Stakeholders */}
            {(isEditing || project.stakeholders?.length > 0) && (
              <>
                <Separator />
                <div className="space-y-3">
                  <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Users className="w-4 h-4" />
                    {txt.stakeholders}
                  </Label>
                  {isEditing ? (
                    <div className="space-y-3">
                      {/* Existing stakeholders with delete */}
                      <div className="flex flex-wrap gap-2">
                        {(editData.stakeholders || []).map((s, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="capitalize flex items-center gap-1 pr-1"
                            data-testid={`stakeholder-badge-${idx}`}
                          >
                            {s.role}: {s.company_id}
                            <button
                              type="button"
                              className="ml-1 rounded-full hover:bg-destructive/20 p-0.5 transition-colors"
                              onClick={() => {
                                const updated = editData.stakeholders.filter((_, i) => i !== idx);
                                setEditData({ ...editData, stakeholders: updated });
                              }}
                              data-testid={`remove-stakeholder-${idx}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                        {(editData.stakeholders || []).length === 0 && (
                          <p className="text-xs text-muted-foreground">{txt.noStakeholders}</p>
                        )}
                      </div>
                      {/* Add new stakeholder */}
                      <div className="flex gap-2 items-end">
                        <div className="space-y-1 w-36">
                          <Label className="text-xs text-muted-foreground">
                            {language === 'ar' ? 'الدور' : 'Role'}
                          </Label>
                          <Select value={newStakeholderRole} onValueChange={setNewStakeholderRole}>
                            <SelectTrigger className="h-8 text-xs" data-testid="new-stakeholder-role">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STAKEHOLDER_ROLES.map(r => (
                                <SelectItem key={r} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1 flex-1">
                          <Label className="text-xs text-muted-foreground">
                            {language === 'ar' ? 'الشركة / الجهة' : 'Company / Entity'}
                          </Label>
                          <Input
                            className="h-8 text-xs"
                            value={newStakeholderName}
                            onChange={(e) => setNewStakeholderName(e.target.value)}
                            placeholder={language === 'ar' ? 'اسم الشركة...' : 'Company name...'}
                            data-testid="new-stakeholder-name"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newStakeholderName.trim()) {
                                setEditData({
                                  ...editData,
                                  stakeholders: [...(editData.stakeholders || []), { role: newStakeholderRole, company_id: newStakeholderName.trim() }]
                                });
                                setNewStakeholderName('');
                              }
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 shrink-0"
                          disabled={!newStakeholderName.trim()}
                          onClick={() => {
                            if (!newStakeholderName.trim()) return;
                            setEditData({
                              ...editData,
                              stakeholders: [...(editData.stakeholders || []), { role: newStakeholderRole, company_id: newStakeholderName.trim() }]
                            });
                            setNewStakeholderName('');
                          }}
                          data-testid="add-stakeholder-btn"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {language === 'ar' ? 'إضافة' : 'Add'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {project.stakeholders.map((stakeholder, idx) => (
                        <Badge key={idx} variant="outline" className="capitalize">
                          {stakeholder.role}: {stakeholder.company_id}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Created By */}
            <Separator />
            <div className="space-y-3">
              <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                <User className="w-4 h-4" />
                {language === 'ar' ? 'أنشئ بواسطة' : 'Created By'}
              </Label>
              <p className="font-medium">
                {project.created_by_name || (project.created_by === 'admin' ? 'Admin' : project.created_by) || 'Admin'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Timestamps Card */}
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-4 space-y-4">
              <div className={`flex items-center gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">{txt.createdAt}:</span>
                <span className="font-medium">{formatDate(project.created_at)}</span>
              </div>
              <div className={`flex items-center gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">{txt.updatedAt}:</span>
                <span className="font-medium">{formatDate(project.updated_at)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Linked Items Card */}
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">
                  {language === 'ar' ? 'العناصر المرتبطة' : 'Linked Items'}
                </CardTitle>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setShowManageLinks(true)}
                    data-testid="manage-links-btn"
                  >
                    <Link2 className="w-3.5 h-3.5 mr-1" />
                    {language === 'ar' ? 'إدارة' : 'Manage'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">

              {/* Linked News — static count, no detail page per item */}
              <div className="p-3 rounded-lg bg-muted/30">
                <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{txt.linkedNews}</span>
                </div>
                <p className="text-2xl font-bold">{project.linked_news_ids?.length || 0}</p>
              </div>

              {/* Linked Tenders — clickable */}
              <div
                className="p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors select-none"
                onClick={() => toggleLinkedSection('tenders', project)}
                data-testid="linked-tenders-toggle"
              >
                <div className={`flex items-center justify-between gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Briefcase className="w-4 h-4" />
                    <span className="text-xs">{txt.linkedTenders}</span>
                  </div>
                  {expandedSection === 'tenders'
                    ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                    : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
                <p className="text-2xl font-bold text-primary">{project.linked_tender_ids?.length || 0}</p>
              </div>

              {expandedSection === 'tenders' && (
                <div className="border rounded-lg overflow-hidden">
                  {loadingLinked ? (
                    <div className="p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : linkedTenderDetails.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">No tenders found</div>
                  ) : (
                    <div className="divide-y">
                      {linkedTenderDetails.map(tender => (
                        <div
                          key={tender._id}
                          className="flex items-start gap-2 p-2.5 hover:bg-muted/40 cursor-pointer transition-colors"
                          onClick={() => navigate(`/dashboard/tenders/${pathId(tender._id)}`)}
                          data-testid={`linked-tender-item-${tender._id}`}
                        >
                          <Briefcase className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium leading-tight line-clamp-2">
                              {(typeof tender.title === 'object' ? (tender.title?.en || tender.title?.ar) : tender.title) || tender.canonical_name}
                            </p>
                            {tender.country && <p className="text-xs text-muted-foreground mt-0.5">{tender.country}</p>}
                          </div>
                          <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Linked Companies — clickable */}
              <div
                className="p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors select-none"
                onClick={() => toggleLinkedSection('companies', project)}
                data-testid="linked-companies-toggle"
              >
                <div className={`flex items-center justify-between gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Building2 className="w-4 h-4" />
                    <span className="text-xs">{txt.linkedCompanies}</span>
                  </div>
                  {expandedSection === 'companies'
                    ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                    : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
                <p className="text-2xl font-bold text-accent">{project.linked_company_ids?.length || 0}</p>
              </div>

              {expandedSection === 'companies' && (
                <div className="border rounded-lg overflow-hidden">
                  {loadingLinked ? (
                    <div className="p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : linkedCompanyDetails.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">No companies found</div>
                  ) : (
                    <div className="divide-y">
                      {linkedCompanyDetails.map(company => (
                        <div
                          key={company._id}
                          className="flex items-start gap-2 p-2.5 hover:bg-muted/40 cursor-pointer transition-colors"
                          onClick={() => navigate(`/dashboard/companies/${company._id}`)}
                          data-testid={`linked-company-item-${company._id}`}
                        >
                          <Building2 className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium leading-tight line-clamp-2">{company.canonical_name}</p>
                            {company.country && <p className="text-xs text-muted-foreground mt-0.5">{company.country}</p>}
                          </div>
                          <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Sub-Projects — only shown for master projects */}
              {project.is_master_project && (
                <>
                  <div
                    className="p-3 rounded-lg bg-indigo-500/5 border border-indigo-500/20 cursor-pointer hover:bg-indigo-500/10 transition-colors select-none"
                    onClick={() => toggleLinkedSection('sub_projects', project)}
                    data-testid="sub-projects-toggle"
                  >
                    <div className={`flex items-center justify-between gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`flex items-center gap-2 text-indigo-600 dark:text-indigo-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Building2 className="w-4 h-4" />
                        <span className="text-xs font-medium">{txt.subProjects}</span>
                      </div>
                      {expandedSection === 'sub_projects'
                        ? <ChevronDown className="w-3.5 h-3.5 text-indigo-500" />
                        : <ChevronRight className="w-3.5 h-3.5 text-indigo-500" />}
                    </div>
                    <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{subProjectCount}</p>
                  </div>

                  {expandedSection === 'sub_projects' && (
                    <div className="border border-indigo-200 rounded-lg overflow-hidden">
                      {loadingLinked ? (
                        <div className="p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                        </div>
                      ) : subProjects.length === 0 ? (
                        <div className="p-3 text-sm text-muted-foreground text-center">No sub-projects linked yet</div>
                      ) : (
                        <div className="divide-y">
                          {subProjects.map(sp => (
                            <div
                              key={sp._id}
                              className="flex items-start gap-2 p-2.5 hover:bg-indigo-500/5 cursor-pointer transition-colors"
                              onClick={() => navigate(`/dashboard/projects/${sp._id}`)}
                              data-testid={`sub-project-item-${sp._id}`}
                            >
                              <Building2 className="w-3.5 h-3.5 text-indigo-500 mt-0.5 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium leading-tight line-clamp-2">{sp.canonical_name}</p>
                                {sp.country && <p className="text-xs text-muted-foreground mt-0.5">{sp.country}</p>}
                              </div>
                              <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Linked Master Projects — clickable */}
              <div
                className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 cursor-pointer hover:bg-blue-500/10 transition-colors select-none"
                onClick={() => toggleLinkedSection('master_projects', project)}
                data-testid="linked-master-projects-toggle"
              >
                <div className={`flex items-center justify-between gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-2 text-blue-600 dark:text-blue-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Building2 className="w-4 h-4" />
                    <span className="text-xs font-medium">{txt.linkedMasterProjects}</span>
                  </div>
                  {expandedSection === 'master_projects'
                    ? <ChevronDown className="w-3.5 h-3.5 text-blue-500" />
                    : <ChevronRight className="w-3.5 h-3.5 text-blue-500" />}
                </div>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{project.linked_master_project_ids?.length || 0}</p>
              </div>

              {expandedSection === 'master_projects' && (
                <div className="border border-blue-200 rounded-lg overflow-hidden">
                  {loadingLinked ? (
                    <div className="p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : linkedMasterProjectDetails.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">No master projects linked</div>
                  ) : (
                    <div className="divide-y">
                      {linkedMasterProjectDetails.map(mp => (
                        <div
                          key={mp._id}
                          className="flex items-start gap-2 p-2.5 hover:bg-blue-500/5 cursor-pointer transition-colors"
                          onClick={() => navigate(`/dashboard/projects/${mp._id}`)}
                          data-testid={`linked-master-project-item-${mp._id}`}
                        >
                          <Building2 className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium leading-tight line-clamp-2">{mp.canonical_name}</p>
                            {mp.country && <p className="text-xs text-muted-foreground mt-0.5">{mp.country}</p>}
                          </div>
                          <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </CardContent>
          </Card>

          {/* Admin Notice */}
          {!isAdmin && (
            <Card className="border-0 bg-amber-500/10 backdrop-blur-sm">
              <CardContent className="p-4">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {language === 'ar' 
                    ? 'تعديل المشروع متاح للمسؤولين فقط' 
                    : 'Project editing is available for admins only'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Manage Links Dialog */}
      {project && (
        <ManageLinksDialog
          open={showManageLinks}
          onOpenChange={setShowManageLinks}
          fromType="projects"
          fromId={project._id}
          fromTitle={project.canonical_name || project.name}
          currentLinks={{
            news:            project.linked_news_ids         || [],
            tenders:         project.linked_tender_ids       || [],
            companies:       project.linked_company_ids      || [],
            master_projects: project.linked_master_project_ids || [],
          }}
          token={token}
          onSuccess={() => { fetchProject(); setExpandedSection(null); }}
        />
      )}
    </div>
  );
}
